import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface Interaction {
  id: string;
  date: string;
  type: 'email' | 'whatsapp' | 'call' | 'meeting' | 'note';
  direction: 'incoming' | 'outgoing';
  content: string;
  subject?: string;
}

export interface Lead {
  id: string;
  uniqueCode: string;
  name: string;
  email: string;
  phone?: string;
  source: string;
  status: string;
  notes?: string;
  lastContactDate?: string;
  createdAt?: string;
  productInterest?: string;
  interactions?: Interaction[];
  avatar?: string;
}

interface LeadState {
  items: Lead[];
  loading: boolean;
  error: string | null;
}

const initialState: LeadState = {
  items: [],
  loading: false,
  error: null,
};

const leadSlice = createSlice({
  name: 'leads',
  initialState,
  reducers: {
    addLead: (state, action: PayloadAction<Lead>) => {
      state.items.push(action.payload);
    },
    setLeads: (state, action: PayloadAction<Lead[]>) => {
      state.items = action.payload;
    },
    updateLeadStatus: (state, action: PayloadAction<{ id: string; status: string }>) => {
      const lead = state.items.find(item => item.id === action.payload.id);
      if (lead) {
        lead.status = action.payload.status;
      }
    },
    updateLead: (state, action: PayloadAction<Lead>) => {
      const index = state.items.findIndex(item => item.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    deleteLead: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(item => item.id !== action.payload);
    },
    addInteraction: (state, action: PayloadAction<{ leadId: string; interaction: Interaction }>) => {
      const lead = state.items.find(item => item.id === action.payload.leadId);
      if (lead) {
        if (!lead.interactions) {
          lead.interactions = [];
        }
        lead.interactions.push(action.payload.interaction);
        lead.lastContactDate = action.payload.interaction.date;
      }
    },
    updateInteraction: (state, action: PayloadAction<{ leadId: string; interactionId: string; interaction: Partial<Interaction> }>) => {
      const lead = state.items.find(item => item.id === action.payload.leadId);
      if (lead && lead.interactions) {
        const interactionIndex = lead.interactions.findIndex(i => i.id === action.payload.interactionId);
        if (interactionIndex !== -1) {
          lead.interactions[interactionIndex] = {
            ...lead.interactions[interactionIndex],
            ...action.payload.interaction
          };
        }
      }
    },
    deleteInteraction: (state, action: PayloadAction<{ leadId: string; interactionId: string }>) => {
      const lead = state.items.find(item => item.id === action.payload.leadId);
      if (lead && lead.interactions) {
        lead.interactions = lead.interactions.filter(i => i.id !== action.payload.interactionId);
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const { 
  addLead, 
  setLeads, 
  updateLeadStatus, 
  updateLead,
  deleteLead,
  addInteraction,
  updateInteraction,
  deleteInteraction,
  setLoading, 
  setError 
} = leadSlice.actions;
export default leadSlice.reducer; 